import { useMemo } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { usePorts } from '../api';
import { ComboBox } from './ComboBox';

export interface PortSelectProps {
  fiscalYear: number;
  /** 事業実施者の港名を選択肢にする。漁協ユーザーは常に自組織 */
  businessOperatorId?: number | null;
  /** 選択中の港名（港名マスタの名称そのもの、または自由入力の文字列） */
  value: string;
  onChange: (value: string) => void;
  /** マスタに無い港名の手入力を許可する（事前連絡の「選択肢に無い港」など） */
  allowFreeText?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  placeholder?: string;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'full';
  'aria-label'?: string;
  'aria-describedby'?: string;
}

/** 港名の選択部品（03_作業分担 §6.3）。港名マスタ（年度・事業実施者別）から選ぶ。 */
export function PortSelect({
  fiscalYear,
  businessOperatorId,
  value,
  onChange,
  allowFreeText,
  placeholder = '港名を選択',
  width = 'sm',
  ...rest
}: PortSelectProps) {
  const { user } = useAuth();
  const own = user?.organizationType === '漁協' && user.businessOperatorId != null ? Number(user.businessOperatorId) : null;
  const opId = own ?? businessOperatorId ?? null;
  // 基金・庁・県は事業実施者が決まるまで選択肢を出さない（全事業実施者の港が混在するため）
  const { data, isPending } = usePorts(fiscalYear, opId, opId != null);
  const options = useMemo(() => {
    const seen = new Set<string>();
    return (data?.items ?? [])
      .filter((p) => (seen.has(p.name) ? false : (seen.add(p.name), true)))
      .map((p) => ({ value: p.name, label: p.name }));
  }, [data]);

  return (
    <ComboBox
      {...rest}
      width={width}
      placeholder={opId == null && !allowFreeText ? '事業実施者を先に選択' : placeholder}
      loading={opId != null && isPending}
      options={options}
      value={value}
      allowFreeText={allowFreeText}
      disabled={rest.disabled || (opId == null && !allowFreeText)}
      onChange={(v) => onChange(v)}
    />
  );
}
